import { SiteSettings } from '@/features/website-builder/schema/site'
import {
  OpeningHours,
  RestaurantAddress,
  RestaurantContact,
  RestaurantIdentity
} from '../schema/settings'
import { WEEKDAY_COUNT } from '../config'

/**
 * Venue facts lifted out of a website document this browser already had.
 *
 * A one-time rescue, not a sync. Before this panel existed the only place to type
 * an address was the website builder, so a venue that has been using it has real
 * data in `localStorage['website-builder-storage']` that would otherwise appear to
 * have vanished. Runs once, guarded by the store's `seededFrom`.
 *
 * The street is **not** split into a street and a building number. Parsing
 * `ul. Miodowa 12/3` correctly means knowing that `12/3` is a building and a
 * flat, that `12A` is one building, and that `Aleja 3 Maja 5` has a number in the
 * street name — a guess here puts wrong data on an invoice, and the owner can
 * separate the two in one keystroke.
 */

export const identityFromSiteSettings = (
  site: SiteSettings
): Pick<RestaurantIdentity, 'name' | 'tagline' | 'description'> => ({
  name: site.name,
  tagline: site.tagline,
  description: site.description
})

export const addressFromSiteSettings = (
  site: SiteSettings
): Partial<RestaurantAddress> => ({
  street: site.street,
  postalCode: site.postalCode,
  city: site.city
})

export const contactFromSiteSettings = (
  site: SiteSettings
): Pick<
  RestaurantContact,
  'phone' | 'email' | 'facebookUrl' | 'instagramUrl'
> => ({
  phone: site.phone,
  email: site.email,
  facebookUrl: site.facebookUrl ?? '',
  instagramUrl: site.instagramUrl ?? ''
})

/** Only a complete week is worth lifting; a partial one is noise. */
export const hoursFromSiteSettings = (
  site: SiteSettings
): OpeningHours | null =>
  site.hours.length === WEEKDAY_COUNT ? site.hours : null
